import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useV2CutoverRedirect from './useV2CutoverRedirect';
import {
  Box, Typography, Card, CardContent, Button, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, LinearProgress, Divider, Collapse, CircularProgress,
  Slider, Stack, Tooltip, Badge, Tabs, Tab, InputAdornment, Popover,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  PlayArrow, CheckCircle, Warning, Refresh, ExitToApp,
  Assignment, Build, Inventory, ExpandMore, ExpandLess,
  Info, Speed, Timeline, Block, Calculate, AccountTree,
  Error as ErrorIcon, CheckBox, IndeterminateCheckBox, Login as LoginIcon,
  Edit, History, Today, Search, CalendarMonth,
  Lock, LockOpen, ArrowForward, Link as LinkIcon, Delete,
  FormatPaint, Add, Close
} from '@mui/icons-material';
import { manufacturingAPI, authAPI, shopFloorAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { StockTreeButton } from './StockTreeViewer';
import usePageTitle from '../../hooks/usePageTitle';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';

// Login Form Component for Shop Floor
function ShopFloorLogin({ operationCode, operationName }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(credentials.username, credentials.password);
      
      if (result.success) {
        toast.success('Login successful!');
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
        toast.error('Login failed');
      }
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      bgcolor: 'background.default',
      p: 3
    }}>
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Build sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Shop Floor Terminal
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {operationName || (operationCode === 'all' ? 'All Operations' : `Operation ${operationCode}`)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Please log in to access the terminal
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Username"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                required
                autoFocus
                autoComplete="username"
                variant="outlined"
                disabled={loading}
              />
              
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                required
                autoComplete="current-password"
                variant="outlined"
                disabled={loading}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading || !credentials.username || !credentials.password}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
              >
                {loading ? 'Logging in...' : 'Login to Terminal'}
              </Button>
            </Stack>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/')}
              disabled={loading}
            >
              Back to Home
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

// Step Indicator Component for Multi-Step Work Orders
function StepIndicator({ workOrder }) {
  if (!workOrder.is_multi_step) return null;
  
  const stepColor = workOrder.step_number === 1 ? 'primary' : 
                    workOrder.step_number === workOrder.total_steps ? 'success' : 'info';
  
  return (
    <Tooltip 
      title={
        <Box>
          <Typography variant="caption" display="block">
            Multi-step routing: {workOrder.step_display}
          </Typography>
          {workOrder.depends_on_number && (
            <Typography variant="caption" display="block">
              Depends on: {workOrder.depends_on_number} ({workOrder.depends_on_status})
            </Typography>
          )}
        </Box>
      }
      arrow
    >
      <Chip
        icon={workOrder.step_number === 1 ? <LockOpen /> : <LinkIcon />}
        label={workOrder.step_display}
        size="small"
        color={stepColor}
        variant="outlined"
        sx={{ 
          fontWeight: 'bold',
          ml: 1,
          '& .MuiChip-icon': { fontSize: '0.9rem' }
        }}
      />
    </Tooltip>
  );
}


// Collapsible Notes Component - Character-based truncation
function CollapsibleNotes({ notes, maxChars = 200 }) {
  const [expanded, setExpanded] = useState(false);

  if (!notes) return null;

  const needsTruncation = notes.length > maxChars;
  const displayText = expanded || !needsTruncation 
    ? notes 
    : notes.substring(0, maxChars).trim() + '...';

  return (
    <Box>
      <Typography variant="body2" component="div">
        <strong>Notes:</strong>
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          mt: 0.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        {displayText}
      </Typography>
      {needsTruncation && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          sx={{ 
            mt: 0.5, 
            p: 0, 
            minWidth: 'auto',
            textTransform: 'none',
            fontSize: '0.75rem'
          }}
        >
          {expanded ? 'Show less' : 'Show more...'}
        </Button>
      )}
    </Box>
  );
}

// Dependency Blocker Alert
function DependencyBlockerAlert({ workOrder }) {
  if (!workOrder.is_multi_step || !workOrder.depends_on_number) return null;

  // Fully blocked - previous step hasn't started (but can be overridden)
  if (!workOrder.can_start) {
    return (
      <Alert
        severity="warning"
        icon={<Lock />}
        sx={{ mt: 1, py: 0.5 }}
      >
        <Typography variant="caption">
          <strong>Pending:</strong> Step {workOrder.step_number - 1} ({workOrder.depends_on_number}) hasn't started yet — you can still override
        </Typography>
      </Alert>
    );
  }

  // Partially available - previous step has some progress but not fully complete
  if (workOrder.depends_on_status !== 'completed' && workOrder.qty_available_from_previous_step > 0) {
    return (
      <Alert
        severity="info"
        sx={{ mt: 1, py: 0.5 }}
      >
        <Typography variant="caption">
          <strong>Partial:</strong> {parseFloat(workOrder.qty_available_from_previous_step)} units released from Step {workOrder.step_number - 1}
        </Typography>
      </Alert>
    );
  }

  return null;
}

// Root Items Display Component with collapsible list
function RootItemsDisplay({ workOrder }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const rootItems = workOrder.root_items_display || [];
  
  if (!rootItems || rootItems.length === 0) {
    return null;
  }
  
  // If the only root item is the same as the output item, don't show it
  if (rootItems.length === 1 && rootItems[0].id === workOrder.output_item) {
    return null;
  }

  const maxVisible = 2;
  const visibleItems = rootItems.slice(0, maxVisible);
  const hiddenItems = rootItems.slice(maxVisible);
  const hasMore = hiddenItems.length > 0;

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  
  return (
    <Box sx={{ mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AccountTree sx={{ fontSize: '0.9rem' }} />
        For:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25, alignItems: 'center' }}>
        {visibleItems.map((item) => (
          <Chip
            key={item.id}
            label={item.name}
            size="small"
            variant="outlined"
            sx={{ 
              height: 26, 
              fontSize: '0.85rem',
              color: 'black',
              borderColor: 'grey.400',
              '& .MuiChip-label': { px: 1.5 }
            }}
          />
        ))}
        {hasMore && (
          <>
            <Chip
              label={`+${hiddenItems.length} more`}
              size="small"
              color="secondary"
              onClick={handleClick}
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                cursor: 'pointer',
                '& .MuiChip-label': { px: 1 },
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white'
                }
              }}
            />
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: { 
                  p: 1.5, 
                  maxWidth: 300,
                  maxHeight: 200,
                  overflow: 'auto'
                }
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                All products ({rootItems.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {rootItems.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{ 
                      height: 22, 
                      fontSize: '0.7rem',
                      justifyContent: 'flex-start',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                ))}
              </Box>
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
}




// Linked Orders Display Component
function LinkedOrdersDisplay({ workOrder, groupDate }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const allLinkedOrders = workOrder.linked_orders_display || [];

  // When the row belongs to a date-grouped virtual entry, only show orders
  // due on that specific date — otherwise the row lists every customer
  // order on the WO regardless of which date group it sits under.
  const linkedOrders = groupDate
    ? allLinkedOrders.filter(o => o.due_date && o.due_date.split('T')[0] === groupDate.split('T')[0])
    : allLinkedOrders;

  if (!linkedOrders || linkedOrders.length === 0) {
    return null;
  }

  const maxVisible = 2;
  const visibleOrders = linkedOrders.slice(0, maxVisible);
  const hiddenOrders = linkedOrders.slice(maxVisible);
  const hasMore = hiddenOrders.length > 0;

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Helper to get display text for an order (now includes qty)
  const getOrderDisplay = (order) => {
    const orderNum = order.sage_order_number || order.order_number;
    if (order.quantity_remaining != null) {
      return `${orderNum} (×${Math.round(order.quantity_remaining)})`;
    }
    return orderNum;
  };

  return (
    <Box sx={{ mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Assignment sx={{ fontSize: '0.9rem' }} />
        Orders:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25, alignItems: 'center' }}>
        {visibleOrders.map((order) => (
          <Tooltip
            key={order.id}
            title={
              <Box>
                <Typography variant="caption" display="block" fontWeight="bold">
                  {order.customer_name}
                </Typography>
                {order.customer_reference && (
                  <Typography variant="caption" display="block">
                    Ref: {order.customer_reference}
                  </Typography>
                )}
                {order.quantity_ordered != null && (
                  <Typography variant="caption" display="block">
                    Qty ordered: {Math.round(order.quantity_ordered)} | Remaining: {Math.round(order.quantity_remaining)}
                  </Typography>
                )}
                {order.due_date && (
                  <Typography variant="caption" display="block">
                    Due: {new Date(order.due_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            }
            arrow
          >
            <Chip
              label={getOrderDisplay(order)}
              size="small"
              variant="outlined"
              color={order.due_date && new Date(order.due_date) < new Date() ? 'error' : 'primary'}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Tooltip>
        ))}
        {hasMore && (
          <>
            <Chip
              label={`+${hiddenOrders.length} more`}
              size="small"
              color="primary"
              onClick={handleClick}
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                cursor: 'pointer',
                '& .MuiChip-label': { px: 1 },
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }}
            />
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: { 
                  p: 1.5, 
                  maxWidth: 300,
                  maxHeight: 200,
                  overflow: 'auto'
                }
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                All orders ({linkedOrders.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {linkedOrders.map((order) => (
                  <Box key={order.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={getOrderDisplay(order)}
                      size="small"
                      variant="outlined"
                      color={order.due_date && new Date(order.due_date) < new Date() ? 'error' : 'primary'}
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        justifyContent: 'flex-start',
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                    {order.due_date && (
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(order.due_date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
}





// Material Availability Calculator Component
function MaterialAvailabilityCalculator({ workOrder, onQuantitySelect }) {
  const [loading, setLoading] = useState(true);
  const [maxProducible, setMaxProducible] = useState(0);
  const [requirements, setRequirements] = useState([]);
  const [testQuantity, setTestQuantity] = useState(1);

  useEffect(() => {
    calculateMaxProducible();
  }, [workOrder.id]);

  const calculateMaxProducible = async () => {
    setLoading(true);
    try {
      const reqResponse = await manufacturingAPI.workOrders.getMaterialRequirements(workOrder.id);
      const reqs = reqResponse.data.requirements || [];
      setRequirements(reqs);

      let maxQty = workOrder.quantity_to_produce * 2;
      
      for (const req of reqs) {
        if (req.required > 0) {
          if (req.available <= 0) {
            maxQty = 0;
            break;
          }
          const maxFromThisMaterial = Math.floor(req.available / req.required);
          maxQty = Math.min(maxQty, maxFromThisMaterial);
        }
      }
      
      maxQty = Math.max(0, maxQty);
      
      setMaxProducible(maxQty);
      setTestQuantity(Math.min(maxQty, workOrder.quantity_to_produce - workOrder.quantity_completed));
    } catch (error) {
      console.error('Failed to calculate max producible:', error);
      setMaxProducible(0);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Alert severity={maxProducible === 0 ? 'error' : 'info'} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Maximum Producible: {maxProducible} units
        </Typography>
        {maxProducible === 0 && (
          <Typography variant="body2">
            Cannot produce any units due to material shortages
          </Typography>
        )}
        {workOrder.is_multi_step && (
          <Typography variant="body2" color="text.secondary">
            Showing materials for {workOrder.step_display} only
          </Typography>
        )}
      </Alert>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Test Production Quantity
        </Typography>
        <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
          <Typography sx={{ minWidth: 50 }}>{testQuantity}</Typography>
          <Slider
            value={testQuantity}
            onChange={(e, val) => setTestQuantity(val)}
            min={1}
            max={Math.max(workOrder.quantity_to_produce, maxProducible, 100)}
            marks={[
              { value: workOrder.quantity_to_produce - workOrder.quantity_completed, label: 'Needed' },
              { value: maxProducible, label: 'Max' }
            ]}
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => onQuantitySelect(testQuantity)}
            disabled={testQuantity > maxProducible && maxProducible > 0}
          >
            Use {testQuantity}
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Material</TableCell>
              <TableCell align="right">Per Unit</TableCell>
              <TableCell align="right">For {testQuantity} Units</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell align="right">After Production</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requirements.map((req, index) => {
              const totalNeeded = req.required * testQuantity;
              const afterProduction = req.available - totalNeeded;
              const hasEnough = afterProduction >= 0;
              
              return (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {req.item_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {req.item_code}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{req.required}</TableCell>
                  <TableCell align="right">
                    <Typography 
                      fontWeight="bold"
                      color={hasEnough ? 'inherit' : 'error.main'}
                    >
                      {totalNeeded}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color={req.available > 0 ? 'success.main' : 'error.main'}>
                      {req.available}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      fontWeight="bold"
                      color={afterProduction >= 0 ? 'success.main' : 'error.main'}
                    >
                      {afterProduction}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={hasEnough ? <CheckCircle /> : <Block />}
                      label={hasEnough ? 'OK' : `Short ${Math.abs(afterProduction)}`}
                      size="small"
                      color={hasEnough ? 'success' : 'error'}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}


function DemandTimelineDisplay({ workOrder }) {
  const [demandData, setDemandData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workOrder.demand_summary && workOrder.demand_timeline) {
      setDemandData({
        summary: workOrder.demand_summary,
        timeline: workOrder.demand_timeline
      });
      setLoading(false);
    }
  }, [workOrder]);

  if (loading || !demandData) {
    return <CircularProgress size={20} />;
  }

  const { summary, timeline } = demandData;

  if (timeline.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          No pending customer orders for this item
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Timeline />
        Demand Timeline
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {summary.overdue > 0 && (
          <Chip 
            label={`${summary.overdue} OVERDUE`}
            color="error"
            icon={<Warning />}
            size="small"
          />
        )}
        {summary.today > 0 && (
          <Chip 
            label={`${summary.today} Today`}
            color="error"
            size="small"
          />
        )}
        {summary.tomorrow > 0 && (
          <Chip 
            label={`${summary.tomorrow} Tomorrow`}
            color="warning"
            size="small"
          />
        )}
        {summary.this_week > 0 && (
          <Chip 
            label={`${summary.this_week} This Week`}
            color="info"
            size="small"
          />
        )}
        {summary.this_month > 0 && (
          <Chip 
            label={`${summary.this_month} This Month`}
            color="default"
            size="small"
          />
        )}
        <Chip 
          label={`Total: ${summary.total} units`}
          variant="outlined"
          size="small"
        />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Customer & Product</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Urgency</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeline.map((demand, index) => (
              <TableRow 
                key={index}
                sx={{
                  backgroundColor: demand.is_linked ? 'action.selected' : 'inherit'
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {demand.customer_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {demand.order_number}
                    {demand.customer_ref && demand.customer_ref !== demand.order_number && (
                      <> - Ref: {demand.customer_ref}</>
                    )}
                  </Typography>
                  {demand.via_parent && (
                    <Box sx={{ mt: 0.5 }}>
                      <Chip 
                        label={`via ${demand.via_parent}`}
                        size="small" 
                        variant="outlined"
                        color="info"
                        sx={{ height: 18, fontSize: '0.7rem' }}
                        icon={<AccountTree sx={{ fontSize: '0.9rem' }} />}
                      />
                      {demand.quantity_per_parent && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({demand.quantity_per_parent} per unit)
                        </Typography>
                      )}
                      {demand.bom_depth && demand.bom_depth > 1 && (
                        <Chip 
                          label={`${demand.bom_depth} levels up`}
                          size="small" 
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.65rem', ml: 0.5 }}
                        />
                      )}
                    </Box>
                  )}
                  {demand.is_linked && (
                    <Chip 
                      label="This WO" 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} 
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {demand.quantity_needed}
                  </Typography>
                  {!demand.via_parent && (
                    <Typography variant="caption" color="text.secondary">
                      of {demand.quantity_ordered}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {demand.due_date ? new Date(demand.due_date).toLocaleDateString() : 'Not set'}
                  </Typography>
                  {demand.days_until_due !== null && (
                    <Typography 
                      variant="caption" 
                      color={demand.days_until_due < 0 ? 'error.main' : 'text.secondary'}
                    >
                      {demand.days_until_due < 0 
                        ? `${Math.abs(demand.days_until_due)} days overdue`
                        : demand.days_until_due === 0
                        ? 'Due today!'
                        : `${demand.days_until_due} days left`
                      }
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={demand.urgency.replace('_', ' ').toUpperCase()}
                    size="small"
                    color={
                      demand.urgency === 'overdue' ? 'error' :
                      demand.urgency === 'today' ? 'error' :
                      demand.urgency === 'tomorrow' ? 'warning' :
                      demand.urgency === 'this_week' ? 'info' :
                      'default'
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Production Priority:</strong> Focus on overdue and today's demands first.
          Total pipeline demand: {summary.total} units across {timeline.length} order{timeline.length !== 1 ? 's' : ''}.
        </Typography>
      </Alert>
    </Box>
  );
}



// Material-Aware Start Button Component with dependency blocking
function MaterialAwareStartButton({ workOrder, onStart }) {
  const [status, setStatus] = useState('loading');
  const [maxProducible, setMaxProducible] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  const remaining = parseFloat(workOrder.quantity_to_produce) - parseFloat(workOrder.quantity_completed);

  // Check if blocked by dependency
  const isBlockedByDependency = workOrder.is_multi_step && !workOrder.can_start;

  // Only fetch when visible in viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    if (isBlockedByDependency) {
      setStatus('blocked');
      setLoading(false);
      return;
    }
    checkAvailability();
  }, [isVisible, workOrder.id, workOrder.can_start, workOrder.quantity_completed]);

  const checkAvailability = async () => {
    setLoading(true);
    try {
      const reqResponse = await manufacturingAPI.workOrders.getMaterialRequirements(workOrder.id);
      const reqs = reqResponse.data.requirements || [];

      if (reqs.length === 0) {
        setStatus('full');
        setMaxProducible(remaining);
        setLoading(false);
        return;
      }

      let maxQty = Infinity;
      
      for (const req of reqs) {
        if (req.required > 0) {
          if (req.available <= 0) {
            maxQty = 0;
            break;
          }
          const maxFromThisMaterial = Math.floor(req.available / req.required);
          maxQty = Math.min(maxQty, maxFromThisMaterial);
        }
      }

      if (maxQty === Infinity) {
        maxQty = remaining;
      }

      // For multi-step: also cap by what's available from the previous step
      if (workOrder.is_multi_step && workOrder.depends_on_number && workOrder.qty_available_from_previous_step != null) {
        const availFromPrev = parseFloat(workOrder.qty_available_from_previous_step);
        maxQty = Math.min(maxQty, availFromPrev);
      }

      maxQty = Math.max(0, maxQty);

      setMaxProducible(maxQty);

      if (maxQty <= 0) {
        setStatus('none');
      } else if (maxQty >= remaining) {
        setStatus('full');
      } else {
        setStatus('partial');
      }
    } catch (error) {
      console.error('Failed to check material availability:', error);
      setStatus('unknown');
      setMaxProducible(0);
    } finally {
      setLoading(false);
    }
  };

  const getButtonColor = () => {
    if (loading) return 'grey.500';
    if (isBlockedByDependency) return 'grey.600';
    switch (status) {
      case 'full':
        return 'success.main';
      case 'partial':
        return 'warning.main';
      case 'none':
        return 'error.main';
      case 'blocked':
        return 'grey.600';
      default:
        return 'grey.500';
    }
  };

  const getButtonText = () => {
    if (loading) return 'Checking...';
    if (parseFloat(workOrder.quantity_completed) >= parseFloat(workOrder.quantity_to_produce)) return 'Complete';
    if (isBlockedByDependency) return `CAN MAKE 0`;
    
    switch (status) {
      case 'full':
        return 'START';
      case 'partial':
        return `CAN MAKE ${maxProducible}`;
      case 'none':
        return 'SHORTAGE';
      case 'blocked':
        return 'BLOCKED';
      default:
        return 'START';
    }
  };

  const getTooltipText = () => {
    if (loading) return 'Checking material availability...';
    if (isBlockedByDependency) {
      return `Step ${workOrder.step_number - 1} hasn't started yet - click to override`;
    }
    // For multi-step with partial previous step progress, mention the constraint
    const prevStepNote = workOrder.is_multi_step && workOrder.depends_on_number && workOrder.depends_on_status !== 'completed'
      ? ` (${parseFloat(workOrder.qty_available_from_previous_step || 0)} released from Step ${workOrder.step_number - 1})`
      : '';
    switch (status) {
      case 'full':
        return `All materials available - can make all ${remaining} units${prevStepNote}`;
      case 'partial':
        return `Can make ${maxProducible} of ${remaining} needed${prevStepNote}`;
      case 'none':
        return 'No materials available - production blocked';
      default:
        return 'Material status unknown';
    }
  };

  const isDisabled = loading ||
    parseFloat(workOrder.quantity_completed) >= parseFloat(workOrder.quantity_to_produce) ||
    status === 'none';

  return (
    <Tooltip title={getTooltipText()} arrow>
      <span ref={ref}>
        <Button
          variant="contained"
          size="large"
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> :
            isBlockedByDependency ? <Lock /> :
            status === 'none' ? <Block /> :
            <PlayArrow />
          }
          onClick={(e) => {
            e.stopPropagation();
            onStart(workOrder);
          }}
          disabled={isDisabled}
          sx={{
            minWidth: 140,
            backgroundColor: getButtonColor(),
            '&:hover': {
              backgroundColor: isBlockedByDependency ? 'grey.700' :
                              status === 'full' ? 'success.dark' :
                              status === 'partial' ? 'warning.dark' :
                              status === 'none' ? 'error.dark' : 'grey.600'
            },
            '&.Mui-disabled': {
              backgroundColor: isBlockedByDependency ? 'grey.500' :
                              status === 'none' ? 'error.light' : 'grey.400',
              color: 'white'
            }
          }}
        >
          {getButtonText()}
        </Button>
      </span>
    </Tooltip>
  );
}


// Enhanced BOM Tree Visualizer Component
function BOMTreeVisualizer({ itemId, quantity, workOrder }) {
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => {
    loadBOMTree();
  }, [itemId, quantity]);

  const loadBOMTree = async () => {
    setLoading(true);
    try {
      const explosionResponse = await manufacturingAPI.bom.explodeRequirements({
        item_id: itemId,
        quantity: quantity
      });

      const bomResponse = await manufacturingAPI.bom.list();
      const allBoms = bomResponse.data.results || bomResponse.data;
      
      const tree = buildHierarchicalTree(
        itemId, 
        quantity, 
        explosionResponse.data,
        allBoms
      );
      
      setTreeData(tree);
      
      const allNodeIds = getAllNodeIds(tree);
      setExpandedNodes(new Set(allNodeIds));
    } catch (error) {
      console.error('Failed to load BOM tree:', error);
      setTreeData(null);
    } finally {
      setLoading(false);
    }
  };

  const buildHierarchicalTree = (rootItemId, rootQuantity, explosionData, allBoms) => {
    const itemMap = new Map();
    explosionData.requirements.forEach(req => {
      itemMap.set(req.item_id, {
        code: req.item_code,
        name: req.item_name,
        available: req.available_quantity,
        required: req.required_quantity,
        hasShortage: req.is_bottleneck
      });
    });

    const childrenMap = new Map();
    allBoms.forEach(bom => {
      if (!childrenMap.has(bom.parent_item)) {
        childrenMap.set(bom.parent_item, []);
      }
      childrenMap.get(bom.parent_item).push({
        childId: bom.child_item,
        childCode: bom.child_item_display,
        quantityRequired: bom.quantity_required
      });
    });

    const buildNode = (itemId, itemCode, quantity, parentQtyMultiplier = 1) => {
      const itemInfo = itemMap.get(itemId) || {};
      
      const node = {
        id: itemId,
        code: itemCode || itemInfo.code || `Item ${itemId}`,
        name: itemInfo.name || '',
        quantity: quantity,
        hasShortage: itemInfo.hasShortage || false,
        available: itemInfo.available || 0,
        required: itemInfo.required || 0,
        children: []
      };

      const children = childrenMap.get(itemId) || [];
      children.forEach(child => {
        const childQty = child.quantityRequired * quantity;
        const childNode = buildNode(
          child.childId,
          child.childCode,
          childQty,
          parentQtyMultiplier * child.quantityRequired
        );
        node.children.push(childNode);
      });

      return node;
    };

    const rootParts = explosionData.item.split(' - ');
    const rootCode = rootParts[0];
    const rootName = rootParts[1] || '';

    const rootNode = {
      id: rootItemId,
      code: rootCode,
      name: rootName,
      quantity: rootQuantity,
      hasShortage: explosionData.total_bottlenecks > 0,
      available: 'N/A',
      required: rootQuantity,
      children: []
    };

    const rootChildren = childrenMap.get(rootItemId) || [];
    rootChildren.forEach(child => {
      const childQty = child.quantityRequired * rootQuantity;
      const childNode = buildNode(
        child.childId,
        child.childCode,
        childQty
      );
      rootNode.children.push(childNode);
    });

    return rootNode;
  };

  const getAllNodeIds = (node) => {
    const ids = [node.id];
    node.children.forEach(child => {
      ids.push(...getAllNodeIds(child));
    });
    return ids;
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const TreeNode = ({ node, level = 0, isLast = true, parentPath = [] }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
          {level > 0 && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  left: -25,
                  top: -25,
                  bottom: isLast ? '50%' : -25,
                  width: 2,
                  bgcolor: 'grey.400'
                }}
              />
              
              <Box
                sx={{
                  position: 'absolute',
                  left: -25,
                  top: '50%',
                  width: 25,
                  height: 2,
                  bgcolor: 'grey.400'
                }}
              />
            </>
          )}
          
          {parentPath.map((isParentLast, idx) => (
            !isParentLast && level - idx - 1 > 0 && (
              <Box
                key={idx}
                sx={{
                  position: 'absolute',
                  left: -25 - ((level - idx - 1) * 50),
                  top: -25,
                  bottom: -25,
                  width: 2,
                  bgcolor: 'grey.400'
                }}
              />
            )
          ))}
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              ml: level * 6.25,
              p: 1.5,
              borderRadius: 1,
              border: '2px solid',
              borderColor: node.hasShortage ? 'error.main' : 'grey.300',
              backgroundColor: node.hasShortage ? '#fff3e0' : 'background.paper',
              cursor: hasChildren ? 'pointer' : 'default',
              minWidth: 350,
              boxShadow: 1,
              '&:hover': {
                boxShadow: 3
              }
            }}
            onClick={() => hasChildren && toggleNode(node.id)}
          >
            {hasChildren && (
              <IconButton size="small" sx={{ p: 0.5, mr: 1 }}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
            
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1" fontWeight="bold" color={node.hasShortage ? 'error.main' : 'text.primary'}>
                {node.name || node.code}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {node.name ? node.code : ''}
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'right', ml: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Need: {node.quantity}
              </Typography>
              {node.available !== 'N/A' && (
                <Typography 
                  variant="caption" 
                  color={node.available >= node.required ? 'success.main' : 'error.main'}
                  fontWeight="bold"
                >
                  Have: {node.available}
                </Typography>
              )}
            </Box>
            
            {node.hasShortage && (
              <Chip
                label="SHORT"
                size="small"
                color="error"
                sx={{ ml: 1 }}
                icon={<Warning />}
              />
            )}
          </Box>
        </Box>
        
        {isExpanded && hasChildren && (
          <Box sx={{ position: 'relative' }}>
            {node.children.map((child, index) => (
              <TreeNode 
                key={child.id} 
                node={child} 
                level={level + 1}
                isLast={index === node.children.length - 1}
                parentPath={[...parentPath, isLast]}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!treeData) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="body1" gutterBottom>
          Unable to load BOM structure
        </Typography>
        <Typography variant="body2">
          Please ensure the BOM is properly configured for this item.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ overflow: 'auto', p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AccountTree color="primary" />
        BOM Structure - Material Requirements Tree
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          This tree shows the complete product structure. Items with <strong style={{ color: '#ed6c02' }}>orange background</strong> have material shortages.
          Click on items to expand/collapse their components.
        </Typography>
      </Alert>
      
      <Box sx={{ 
        backgroundColor: 'background.paper', 
        p: 3, 
        borderRadius: 2,
        overflowX: 'auto'
      }}>
        <TreeNode node={treeData} />
      </Box>
    </Box>
  );
}


// Enhanced Production Dialog Component with step info
function EnhancedProductionDialog({ open, onClose, selectedWorkOrder, onComplete }) {
  const [productionData, setProductionData] = useState({
    quantity_completed: '',
    quantity_scrapped: '0',
    notes: '',
    priority_customer_order_id: ''
  });
  const [showCalculator, setShowCalculator] = useState(false);
  const [showTreeView, setShowTreeView] = useState(false);
  const [ordersNeeding, setOrdersNeeding] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const isFinalStep = selectedWorkOrder &&
    (!selectedWorkOrder.is_multi_step || selectedWorkOrder.step_number === selectedWorkOrder.total_steps);

  useEffect(() => {
    if (selectedWorkOrder) {
      const remaining = parseFloat(selectedWorkOrder.quantity_to_produce) - parseFloat(selectedWorkOrder.quantity_completed);
      // For multi-step, cap to what's available from the previous step
      const availableFromPrev = selectedWorkOrder.is_multi_step && selectedWorkOrder.depends_on_number
        ? parseFloat(selectedWorkOrder.qty_available_from_previous_step || 0)
        : remaining;
      const maxAllowed = Math.min(remaining, availableFromPrev);
      // If opened from a date-grouped row, prefill with the date's quantity (capped to max allowed)
      const prefill = selectedWorkOrder._groupQty != null
        ? Math.min(parseFloat(selectedWorkOrder._groupQty), maxAllowed)
        : maxAllowed;
      setProductionData({
        quantity_completed: prefill.toString(),
        quantity_scrapped: '0',
        notes: '',
        priority_customer_order_id: ''
      });
    }
  }, [selectedWorkOrder]);

  useEffect(() => {
    if (open && isFinalStep && selectedWorkOrder?.output_item) {
      setLoadingOrders(true);
      manufacturingAPI.workOrders.ordersNeedingItem(selectedWorkOrder.output_item)
        .then(res => setOrdersNeeding(res.data))
        .catch(() => setOrdersNeeding([]))
        .finally(() => setLoadingOrders(false));
    } else {
      setOrdersNeeding([]);
    }
  }, [open, isFinalStep, selectedWorkOrder?.output_item]);

  const handleQuantitySelect = (qty) => {
    setProductionData(prev => ({
      ...prev,
      quantity_completed: qty.toString()
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Speed />
          Record Production: {selectedWorkOrder?.work_order_number}
          {selectedWorkOrder?.is_multi_step && (
            <Chip 
              label={selectedWorkOrder.step_display}
              size="small"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedWorkOrder && (
          <Box sx={{ mt: 2 }}>
            {/* Multi-step info alert */}
            {selectedWorkOrder.is_multi_step && (
              <Alert
                severity="info"
                icon={<LinkIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  <strong>Multi-Step Routing:</strong> This is {selectedWorkOrder.step_display} for this item.
                  {selectedWorkOrder.step_number < selectedWorkOrder.total_steps && (
                    <> After completing this step, those units will be released for Step {selectedWorkOrder.step_number + 1}.</>
                  )}
                  {selectedWorkOrder.step_number === selectedWorkOrder.total_steps && (
                    <> This is the final step - completing it will produce the finished item.</>
                  )}
                </Typography>
                {selectedWorkOrder.depends_on_number && selectedWorkOrder.depends_on_status !== 'completed' && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>{parseFloat(selectedWorkOrder.qty_available_from_previous_step || 0)}</strong> units released from Step {selectedWorkOrder.step_number - 1} so far.
                  </Typography>
                )}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Making: {selectedWorkOrder.output_item_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedWorkOrder.output_item_display}
                  </Typography>
                  <Typography>Operation: {selectedWorkOrder.operation_display}</Typography>
                  <Typography>Remaining: <strong>{selectedWorkOrder.quantity_to_produce - selectedWorkOrder.quantity_completed}</strong> units</Typography>
                  {/* Show root items in dialog */}
                  {selectedWorkOrder.root_items_display && selectedWorkOrder.root_items_display.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>For:</strong>{' '}
                        {selectedWorkOrder.root_items_display.map(item => item.name).join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Alert>

                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Quantity Completed *"
                    type="number"
                    value={productionData.quantity_completed}
                    onChange={(e) => setProductionData(prev => ({
                      ...prev,
                      quantity_completed: e.target.value
                    }))}
                    inputProps={{ min: 0.01, step: 0.01 }}
                    helperText="How many good units did you make?"
                    variant="outlined"
                    size="large"
                  />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Calculate />}
                    onClick={() => setShowCalculator(!showCalculator)}
                    fullWidth
                  >
                    {showCalculator ? 'Hide' : 'Show'} Material Calculator
                  </Button>

                  <TextField
                    fullWidth
                    label="Quantity Scrapped"
                    type="number"
                    value={productionData.quantity_scrapped}
                    onChange={(e) => setProductionData(prev => ({
                      ...prev,
                      quantity_scrapped: e.target.value
                    }))}
                    inputProps={{ min: 0 }}
                    helperText="How many bad units (optional)"
                    variant="outlined"
                  />

                  <TextField
                    fullWidth
                    label="Notes (optional)"
                    multiline
                    rows={3}
                    value={productionData.notes}
                    onChange={(e) => setProductionData(prev => ({
                      ...prev,
                      notes: e.target.value
                    }))}
                    placeholder="Any issues, observations, or special notes..."
                    variant="outlined"
                  />

                  {isFinalStep && (
                    <FormControl fullWidth>
                      <InputLabel>Allocate to order (optional)</InputLabel>
                      <Select
                        value={productionData.priority_customer_order_id}
                        label="Allocate to order (optional)"
                        onChange={(e) => setProductionData(prev => ({
                          ...prev,
                          priority_customer_order_id: e.target.value
                        }))}
                        disabled={loadingOrders}
                      >
                        <MenuItem value="">
                          <em>Auto — earliest due date first</em>
                        </MenuItem>
                        {ordersNeeding.map(order => (
                          <MenuItem key={order.id} value={order.id}>
                            {order.order_number}
                            {order.customer_name ? ` — ${order.customer_name}` : ''}
                            {order.due_date ? ` (due ${new Date(order.due_date).toLocaleDateString()})` : ''}
                            {` · needs ${order.still_needed}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                {showCalculator && (
                  <MaterialAvailabilityCalculator
                    workOrder={selectedWorkOrder}
                    onQuantitySelect={handleQuantitySelect}
                  />
                )}
                
                <Button
                  variant="outlined"
                  startIcon={<AccountTree />}
                  onClick={() => setShowTreeView(!showTreeView)}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {showTreeView ? 'Hide' : 'Show'} BOM Tree View
                </Button>

                {showTreeView && (
                  <Box sx={{ mt: 2 }}>
                    <BOMTreeVisualizer
                      itemId={selectedWorkOrder.output_item}
                      quantity={parseInt(productionData.quantity_completed) || 1}
                      workOrder={selectedWorkOrder}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={onClose}
          size="large"
        >
          Cancel
        </Button>
        <Button 
          onClick={() => onComplete(productionData)} 
          variant="contained"
          startIcon={<CheckCircle />}
          size="large"
          sx={{ minWidth: 160 }}
          disabled={!productionData.quantity_completed || parseInt(productionData.quantity_completed) <= 0}
        >
          Record Production
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Material Status Chip Component
function MaterialStatusChip({ workOrder }) {
  const [materialStatus, setMaterialStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  // Only fetch when visible in viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    checkMaterialStatus();
  }, [isVisible, workOrder.id]);

  const checkMaterialStatus = async () => {
    setLoading(true);
    try {
      const response = await manufacturingAPI.workOrders.checkMaterialAvailability(
        workOrder.id,
        1
      );
      setMaterialStatus(response.data);
    } catch (error) {
      console.error('Failed to check material status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !isVisible) {
    return <span ref={ref}><Chip label="Checking..." size="small" color="default" /></span>;
  }

  if (!materialStatus) {
    return <span ref={ref}><Chip label="Unknown" size="small" color="default" /></span>;
  }

  if (materialStatus.shortages && materialStatus.shortages.length > 0) {
    return (
      <Chip 
        label={`${materialStatus.shortages.length} Material${materialStatus.shortages.length > 1 ? 's' : ''} Missing`}
        size="small" 
        color="error"
        icon={<Warning />}
        onClick={() => {
          alert(`Missing materials:\n${materialStatus.shortages.map(s => 
            `- ${s.item_code}: Need ${s.shortage} more`
          ).join('\n')}`);
        }}
        sx={{ cursor: 'pointer' }}
      />
    );
  }

  return (
    <Chip 
      label="Materials Ready" 
      size="small" 
      color="success"
      icon={<CheckCircle />}
    />
  );
}

// Material Requirements Table Component with total needed for remaining quantity
function MaterialRequirementsTable({ workOrderId, workOrder }) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);

  // Calculate remaining quantity to produce
  const remaining = workOrder 
    ? parseFloat(workOrder.quantity_to_produce) - parseFloat(workOrder.quantity_completed)
    : 0;

  useEffect(() => {
    loadRequirements();
  }, [workOrderId]);

  const loadRequirements = async () => {
    try {
      const response = await manufacturingAPI.workOrders.getMaterialRequirements(workOrderId);
      setRequirements(response.data.requirements || []);
    } catch (error) {
      console.error('Failed to load material requirements:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CircularProgress size={20} />;
  if (requirements.length === 0) return <Typography variant="body2">No material requirements for this step</Typography>;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Inventory />
        Material Requirements
        {workOrder?.is_multi_step && (
          <Chip 
            label={`For ${workOrder.step_display}`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
        <Chip 
          label={`${remaining} units remaining`}
          size="small"
          variant="outlined"
          color="primary"
        />
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Material</TableCell>
            <TableCell align="right">Per Unit</TableCell>
            <TableCell align="right">Total Needed</TableCell>
            <TableCell align="right">Available</TableCell>
            <TableCell align="right">Shortage</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requirements.map((req, index) => {
            const totalNeeded = req.required * remaining;
            const shortage = Math.max(0, totalNeeded - req.available);
            const isShort = shortage > 0;
            
            return (
              <TableRow 
                key={index}
                sx={{ 
                  backgroundColor: isShort ? '#fff3e0' : '#e8f5e9',
                  '& td': {
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider'
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {req.item_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {req.item_code}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {req.required}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    color={isShort ? 'error.main' : 'inherit'}
                  >
                    {totalNeeded}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    color={req.available >= totalNeeded ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                  >
                    {req.available}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {shortage > 0 ? (
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      -{shortage}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={isShort ? 'SHORT' : 'READY'}
                    size="small"
                    color={isShort ? 'error' : 'success'}
                    icon={isShort ? <Block /> : <CheckCircle />}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// Material Shortage Dialog Component
function MaterialShortageDialog({ open, onClose, workOrder, shortages }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Block />
          Production Blocked - Material Shortage
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Cannot proceed with work order: {workOrder}
          </Typography>
          <Typography>
            The following materials are not available in sufficient quantities:
          </Typography>
        </Alert>
        
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Material</TableCell>
              <TableCell align="right">Required</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell align="right">Shortage</TableCell>
              <TableCell>Action Needed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shortages.map((shortage, index) => (
              <TableRow key={index} sx={{ backgroundColor: '#fff3e0' }}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {shortage.item_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {shortage.item_code}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {shortage.required}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="error.main">
                    {shortage.available}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="error.main" fontWeight="bold">
                    -{shortage.shortage}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label="Complete prerequisite work orders"
                    size="small"
                    color="warning"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Next Steps:</strong>
          </Typography>
          <Typography variant="body2">
            1. Complete work orders for missing materials first<br/>
            2. Or add stock via Inventory Management<br/>
            3. Then return to complete this work order
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Understood
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditCompletedWorkOrderDialog({ open, onClose, workOrder, onSave }) {
  const [editData, setEditData] = useState({
    quantity_completed: 0,
    quantity_scrapped: 0,
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (workOrder) {
      setEditData({
        quantity_completed: workOrder.quantity_completed || 0,
        quantity_scrapped: workOrder.quantity_scrapped || 0,
        notes: workOrder.notes || ''
      });
    }
  }, [workOrder]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(workOrder.id, editData);
      onClose();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to update work order');
    } finally {
      setLoading(false);
    }
  };

  if (!workOrder) return null;

  const originalQty = workOrder.quantity_completed || 0;
  const newQty = editData.quantity_completed;
  const stockDifference = newQty - originalQty;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit />
          Edit Work Order: {workOrder.work_order_number}
          {workOrder.is_multi_step && (
            <Chip 
              label={workOrder.step_display}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2">
            You are editing a completed work order from today. Changes will affect stock levels.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">Output Item</Typography>
          <Typography variant="body1" fontWeight="bold">{workOrder.output_item_name}</Typography>
          <Typography variant="body2" color="text.secondary">{workOrder.output_item_display}</Typography>
        </Box>

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Quantity Completed"
            type="number"
            value={editData.quantity_completed}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              quantity_completed: parseInt(e.target.value) || 0
            }))}
            inputProps={{ min: 0 }}
            variant="outlined"
            helperText={`Original: ${originalQty}`}
          />

          {/* Stock change preview */}
          {stockDifference !== 0 && (
            <Alert 
              severity={stockDifference > 0 ? 'success' : 'error'}
              icon={stockDifference > 0 ? <Inventory /> : <Warning />}
            >
              <Typography variant="body2">
                <strong>Stock Impact:</strong>{' '}
                {stockDifference > 0 
                  ? `+${stockDifference} units will be added to stock`
                  : `${stockDifference} units will be removed from stock`
                }
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            label="Quantity Scrapped"
            type="number"
            value={editData.quantity_scrapped}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              quantity_scrapped: parseInt(e.target.value) || 0
            }))}
            inputProps={{ min: 0 }}
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={editData.notes}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              notes: e.target.value
            }))}
            variant="outlined"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="warning"
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          disabled={loading}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AdminEditWorkOrderDialog({ open, onClose, workOrder, onSave }) {
  const [editData, setEditData] = useState({
    quantity_to_produce: 0,
    quantity_completed: 0,
    quantity_scrapped: 0,
    customer_order_ref: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (workOrder) {
      setEditData({
        quantity_to_produce: parseFloat(workOrder.quantity_to_produce) || 0,
        quantity_completed: parseFloat(workOrder.quantity_completed) || 0,
        quantity_scrapped: parseFloat(workOrder.quantity_scrapped) || 0,
        customer_order_ref: workOrder.customer_order_ref || '',
        notes: workOrder.notes || ''
      });
    }
  }, [workOrder]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(workOrder.id, editData);
      onClose();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to update work order');
    } finally {
      setLoading(false);
    }
  };

  if (!workOrder) return null;

  // Calculate remaining (what terminal shows as "Need Now")
  const remaining = Math.max(0, editData.quantity_to_produce - editData.quantity_completed);
  const progressPercentage = editData.quantity_to_produce > 0 
    ? Math.round((editData.quantity_completed / editData.quantity_to_produce) * 100) 
    : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit />
          Admin Edit: {workOrder.work_order_number}
          {workOrder.is_multi_step && (
            <Chip 
              label={workOrder.step_display}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2">
            <strong>Admin Override:</strong> You are directly editing work order values. 
            This will NOT affect stock levels - only the work order record will be updated.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">Output Item</Typography>
          <Typography variant="body1" fontWeight="bold">{workOrder.output_item_name}</Typography>
          <Typography variant="body2" color="text.secondary">{workOrder.output_item_display}</Typography>
        </Box>

        {/* Read-only summary showing what terminal sees */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Terminal View (calculated)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box>
              <Typography variant="h4" color="error.main" fontWeight="bold">
                {remaining}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                of {editData.quantity_to_produce} total
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={progressPercentage}
                  sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                  color={progressPercentage >= 100 ? 'success' : 'primary'}
                />
                <Typography variant="body2" fontWeight="bold">
                  {progressPercentage}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {editData.quantity_completed} completed
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Total Quantity to Produce"
            type="number"
            value={editData.quantity_to_produce}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              quantity_to_produce: parseFloat(e.target.value) || 0
            }))}
            inputProps={{ min: 0, step: 0.01 }}
            variant="outlined"
            helperText={`Original: ${workOrder.quantity_to_produce}`}
          />

          <TextField
            fullWidth
            label="Quantity Completed"
            type="number"
            value={editData.quantity_completed}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              quantity_completed: parseFloat(e.target.value) || 0
            }))}
            inputProps={{ min: 0, step: 0.01 }}
            variant="outlined"
            helperText={`Original: ${parseFloat(workOrder.quantity_completed) || 0}`}
          />

          <TextField
            fullWidth
            label="Quantity Scrapped"
            type="number"
            value={editData.quantity_scrapped}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              quantity_scrapped: parseFloat(e.target.value) || 0
            }))}
            inputProps={{ min: 0, step: 0.01 }}
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Customer"
            value={editData.customer_order_ref}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              customer_order_ref: e.target.value
            }))}
            variant="outlined"
            inputProps={{ maxLength: 50 }}
            helperText={`Shown on the shop floor card${workOrder.customer_order_ref ? ` — original: ${workOrder.customer_order_ref}` : ''}`}
          />

          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={editData.notes}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              notes: e.target.value
            }))}
            variant="outlined"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          disabled={loading}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function DeleteWorkOrderDialog({ open, onClose, workOrder, onDelete }) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete(workOrder.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete work order');
    } finally {
      setLoading(false);
    }
  };

  if (!workOrder) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning />
          Delete Work Order: {workOrder.work_order_number}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> This action cannot be undone. The work order record will be permanently deleted.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Output Item</Typography>
          <Typography variant="body1" fontWeight="bold">{workOrder.output_item_name}</Typography>
          <Typography variant="body2" color="text.secondary">{workOrder.output_item_display}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Progress</Typography>
          <Typography variant="body1">
            {workOrder.quantity_completed} of {workOrder.quantity_to_produce} completed
          </Typography>
        </Box>

        {parseFloat(workOrder.quantity_completed) > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> The {workOrder.quantity_completed} units already completed will remain in stock.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} /> : <Warning />}
          disabled={loading}
        >
          Delete Work Order
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// Natural sorting function for alphanumeric strings
function naturalSort(a, b) {
  const regex = /(\d+)|(\D+)/g;
  const aParts = a.match(regex);
  const bParts = b.match(regex);
  
  for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
    const aPart = aParts[i];
    const bPart = bParts[i];
    
    const aIsNum = !isNaN(aPart);
    const bIsNum = !isNaN(bPart);
    
    if (aIsNum && bIsNum) {
      const diff = parseInt(aPart) - parseInt(bPart);
      if (diff !== 0) return diff;
    } else {
      const diff = aPart.localeCompare(bPart);
      if (diff !== 0) return diff;
    }
  }
  
  return aParts.length - bParts.length;
}

// Helper to check if date is today
function isToday(dateString) {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}


const SORT_OPTIONS = [
  { value: 'item_name_asc', label: 'Item Name (A-Z)' },
  { value: 'item_name_desc', label: 'Item Name (Z-A)' },
  { value: 'urgency', label: 'Urgency (Most Urgent First)' },
  { value: 'due_date_asc', label: 'Due Date (Earliest First)' },
  { value: 'due_date_desc', label: 'Due Date (Latest First)' },
  { value: 'wo_number_asc', label: 'Work Order # (Ascending)' },
  { value: 'wo_number_desc', label: 'Work Order # (Descending)' },
];



// Main Component
export default function ShopFloorTerminal() {
  const { operationCode } = useParams();
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();

  // After the v1→v2 cutover, this legacy terminal forwards to the v2 page
  // for the SAME operation (codes are shared between the systems).
  useV2CutoverRedirect(`/manufacturing/v2/shop-floor/${operationCode || ''}`);

  const getStoredSortPreference = () => {
    try {
      const stored = localStorage.getItem(`shopFloorSort_${user?.id || 'default'}`);
      return stored || 'item_name_asc';
    } catch {
      return 'item_name_asc';
    }
  };
  
  const [operation, setOperation] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [completedWorkOrders, setCompletedWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [productionDialog, setProductionDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [operations, setOperations] = useState([]);
  const [materialShortageDialog, setMaterialShortageDialog] = useState({
    open: false,
    workOrder: null,
    shortages: []
  });
  
  // New state for tabs and completed orders
  const [activeTab, setActiveTab] = useState(0);
  const [completedSearchTerm, setCompletedSearchTerm] = useState('');
  const [completedDate, setCompletedDate] = useState(new Date());
  // Ref so the 30s auto-refresh interval (whose closure is created once per
  // operation/user) always fetches completed orders for the currently
  // selected date instead of the date captured at mount
  const completedDateRef = useRef(completedDate);
  useEffect(() => { completedDateRef.current = completedDate; }, [completedDate]);
  const [editDialog, setEditDialog] = useState({ open: false, workOrder: null });
  const [adminEditDialog, setAdminEditDialog] = useState({ open: false, workOrder: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, workOrder: null });

  // Text highlighting (visible to all, editable by admins)
  const [highlightRules, setHighlightRules] = useState([]);     // product-code-specific rules
  const [globalHighlights, setGlobalHighlights] = useState([]); // global text highlights (apply to all WOs)
  const [showHighlightBar, setShowHighlightBar] = useState(false);
  const [showGlobalHighlightBar, setShowGlobalHighlightBar] = useState(false);
  const highlightSaveTimeout = useRef(null);

  // Load highlight rules from server on mount
  useEffect(() => {
    if (!user) return;
    shopFloorAPI.getHighlights().then(res => {
      const allRules = res.data || [];
      const globals = allRules.filter(r => r.product_code === '*');
      const productRules = allRules.filter(r => r.product_code !== '*');
      setGlobalHighlights(globals.map(r => r.highlight_text));
      setHighlightRules(productRules.map(r => ({
        id: r.id,
        productCode: r.product_code,
        highlightText: r.highlight_text,
      })));
    }).catch(() => {});
  }, [user?.id]);

  // Debounced save ALL highlights (product rules + global) to server (admin only)
  const saveAllHighlights = (productRules, globals) => {
    if (!user?.is_staff) return;
    if (highlightSaveTimeout.current) clearTimeout(highlightSaveTimeout.current);
    highlightSaveTimeout.current = setTimeout(() => {
      const toSave = productRules
        .filter(r => r.productCode.trim() && r.highlightText.trim())
        .map(r => ({ product_code: r.productCode.trim(), highlight_text: r.highlightText.trim() }));
      for (const g of globals) {
        const trimmed = g.trim();
        if (trimmed) toSave.push({ product_code: '*', highlight_text: trimmed });
      }
      shopFloorAPI.saveHighlights(toSave).catch(() => {});
    }, 800);
  };

  // Refs to track dialog state and prevent disruptive refreshes
  const isInitialLoad = useRef(true);
  const dialogOpenRef = useRef(false);
  const loadingInProgress = useRef(false);

  const [groupByDate, setGroupByDate] = useState(() => {
    // Initialize from user's server-side preferences if available, fall back to localStorage
    if (user?.ui_preferences?.shopFloorGroupByDate != null) {
      return user.ui_preferences.shopFloorGroupByDate;
    }
    try {
      return localStorage.getItem(`shopFloorGroupByDate_${user?.id || 'default'}`) === 'true';
    } catch { return false; }
  });
  const [filterDate, setFilterDate] = useState(null); // null = show all dates, Date object = show only that day
  const [sortOrder, setSortOrder] = useState(getStoredSortPreference);

  // Sync preferences from server when user loads (covers cross-terminal sync)
  useEffect(() => {
    if (user?.id) {
      authAPI.getPreferences().then(res => {
        const prefs = res.data;
        if (prefs.shopFloorGroupByDate != null) {
          setGroupByDate(prefs.shopFloorGroupByDate);
        }
        if (prefs.shopFloorSort && SORT_OPTIONS.some(opt => opt.value === prefs.shopFloorSort)) {
          setSortOrder(prefs.shopFloorSort);
        }
      }).catch(() => {});
    }
  }, [user?.id]);

  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    authAPI.updatePreferences({ shopFloorSort: newSort }).catch(() => {});
  };

  const handleGroupByDateToggle = () => {
    const newVal = !groupByDate;
    setGroupByDate(newVal);
    if (!newVal) setFilterDate(null); // clear date filter when turning off grouping
    authAPI.updatePreferences({ shopFloorGroupByDate: newVal }).catch(() => {});
  };

  const handleFilterDateChange = (date) => {
    setFilterDate(date);
    // If a date is picked, ensure groupByDate is on
    if (date && !groupByDate) {
      setGroupByDate(true);
      authAPI.updatePreferences({ shopFloorGroupByDate: true }).catch(() => {});
    }
  };

  // Group work orders by due date category
  // Color for date group headers based on proximity
  const getDateGroupColor = (dateStr) => {
    if (!dateStr || dateStr === 'no-date') return 'text.disabled';
    const days = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'error.main';
    if (days === 0) return 'error.main';
    if (days === 1) return 'warning.main';
    if (days <= 7) return 'info.main';
    if (days <= 14) return 'info.light';
    return 'text.secondary';
  };

  // Group work orders by specific due date, splitting WOs across multiple dates
  // based on per-order demand breakdown
  const getGroupedWorkOrders = (orders) => {
    if (!groupByDate) return null;

    const dateMap = {}; // date_key -> { label, date, orders: [] }

    for (const wo of orders) {
      const breakdown = wo.date_quantity_breakdown;

      if (breakdown && breakdown.length > 0) {
        // WO has per-date breakdown - create a virtual entry per date
        for (const entry of breakdown) {
          const dateKey = entry.date ? entry.date.split('T')[0] : 'no-date';
          const label = entry.date_display || 'No Due Date';

          if (!dateMap[dateKey]) {
            dateMap[dateKey] = { label, date: dateKey, orders: [] };
          }
          dateMap[dateKey].orders.push({
            ...wo,
            _groupDate: entry.date,
            _groupDateDisplay: entry.date_display,
            _groupQty: entry.quantity,
            _groupOrders: entry.orders,
            _virtualKey: `${wo.id}-${dateKey}`,
          });
        }
      } else {
        // Fallback: use WO's own due_date
        const dateKey = wo.due_date
          ? new Date(wo.due_date).toISOString().split('T')[0]
          : 'no-date';
        const label = wo.due_date
          ? new Date(wo.due_date).toLocaleDateString('en-GB', {
              weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
            })
          : 'No Due Date';

        if (!dateMap[dateKey]) {
          dateMap[dateKey] = { label, date: dateKey, orders: [] };
        }
        dateMap[dateKey].orders.push({
          ...wo,
          _groupDate: dateKey !== 'no-date' ? dateKey : null,
          _groupDateDisplay: label,
          _groupQty: null, // null = show original quantity
          _virtualKey: `${wo.id}-${dateKey}`,
        });
      }
    }

    // If filtering to a specific date, only return that date's group
    if (filterDate) {
      const filterKey = filterDate.toISOString().split('T')[0];
      if (dateMap[filterKey]) {
        return [dateMap[filterKey]];
      }
      return []; // no WOs for that date
    }

    // Sort chronologically, no-date at end
    const sortedKeys = Object.keys(dateMap).sort((a, b) => {
      if (a === 'no-date') return 1;
      if (b === 'no-date') return -1;
      return a.localeCompare(b);
    });

    return sortedKeys.map(key => dateMap[key]);
  };

  const getSortedWorkOrders = (orders) => {
    const sorted = [...orders];
    
    switch (sortOrder) {
      case 'item_name_asc':
        sorted.sort((a, b) => {
          const nameA = (a.output_item_name || '').toLowerCase();
          const nameB = (b.output_item_name || '').toLowerCase();
          if (nameA !== nameB) return nameA.localeCompare(nameB);
          return (a.step_number || 1) - (b.step_number || 1);
        });
        break;
      case 'item_name_desc':
        sorted.sort((a, b) => {
          const nameA = (a.output_item_name || '').toLowerCase();
          const nameB = (b.output_item_name || '').toLowerCase();
          if (nameA !== nameB) return nameB.localeCompare(nameA);
          return (a.step_number || 1) - (b.step_number || 1);
        });
        break;
      case 'urgency':
        sorted.sort((a, b) => {
          const getUrgencyScore = (wo) => {
            if (wo.demand_summary?.overdue > 0) return 0;
            if (wo.demand_summary?.today > 0) return 1;
            if (wo.demand_summary?.tomorrow > 0) return 2;
            if (wo.demand_summary?.this_week > 0) return 3;
            if (wo.demand_summary?.this_month > 0) return 4;
            if (!wo.due_date) return 10;
            const days = Math.ceil((new Date(wo.due_date) - new Date()) / (1000 * 60 * 60 * 24));
            if (days < 0) return 0;
            if (days <= 1) return 1;
            if (days <= 7) return 3;
            return 5;
          };
          return getUrgencyScore(a) - getUrgencyScore(b);
        });
        break;
      case 'due_date_asc':
        sorted.sort((a, b) => {
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date) - new Date(b.due_date);
        });
        break;
      case 'due_date_desc':
        sorted.sort((a, b) => {
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(b.due_date) - new Date(a.due_date);
        });
        break;
      case 'wo_number_asc':
        sorted.sort((a, b) => naturalSort(a.work_order_number, b.work_order_number));
        break;
      case 'wo_number_desc':
        sorted.sort((a, b) => naturalSort(b.work_order_number, a.work_order_number));
        break;
      default:
        break;
    }
    
    return sorted;
  };

  // --- Admin Text Highlighting ---
  const addHighlightRule = () => {
    const newRules = [...highlightRules, { productCode: '', highlightText: '' }];
    setHighlightRules(newRules);
  };

  const updateHighlightRule = (index, field, value) => {
    const newRules = highlightRules.map((rule, i) =>
      i === index ? { ...rule, [field]: value } : rule
    );
    setHighlightRules(newRules);
    saveAllHighlights(newRules, globalHighlights);
  };

  const removeHighlightRule = (index) => {
    const newRules = highlightRules.filter((_, i) => i !== index);
    setHighlightRules(newRules);
    saveAllHighlights(newRules, globalHighlights);
  };

  // Global highlight CRUD
  const addGlobalHighlight = () => {
    const newGlobals = [...globalHighlights, ''];
    setGlobalHighlights(newGlobals);
  };

  const updateGlobalHighlight = (index, value) => {
    const newGlobals = globalHighlights.map((g, i) => i === index ? value : g);
    setGlobalHighlights(newGlobals);
    saveAllHighlights(highlightRules, newGlobals);
  };

  const removeGlobalHighlight = (index) => {
    const newGlobals = globalHighlights.filter((_, i) => i !== index);
    setGlobalHighlights(newGlobals);
    saveAllHighlights(highlightRules, newGlobals);
  };

  // Get array of text strings to highlight for a given work order
  const getHighlightsForWO = (wo) => {
    const highlights = [];
    // Global highlights apply to all work orders
    for (const g of globalHighlights) {
      const trimmed = g.trim();
      if (trimmed) highlights.push(trimmed);
    }
    // Product-code-specific highlights
    if (highlightRules.length) {
      const woCode = (wo.output_item_display || '').toLowerCase();
      const woName = (wo.output_item_name || '').toLowerCase();
      for (const rule of highlightRules) {
        const code = (rule.productCode || '').trim().toLowerCase();
        const text = (rule.highlightText || '').trim();
        if (!code || !text) continue;
        if (woCode.includes(code) || woName.includes(code)) {
          highlights.push(text);
        }
      }
    }
    return highlights;
  };

  // Apply highlighting to a text string, returning React elements
  const applyHighlight = (text, highlights) => {
    if (!highlights || highlights.length === 0 || !text) return text;
    const escaped = highlights
      .filter(h => h && h.length > 0)
      .map(h => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (escaped.length === 0) return text;
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
    const parts = String(text).split(regex);
    if (parts.length <= 1) return text;
    return (
      <>
        {parts.map((part, i) =>
          i % 2 === 1
            ? <mark key={i} style={{ backgroundColor: '#ffeb3b', padding: '0 2px', borderRadius: '2px', fontWeight: 'bold' }}>{part}</mark>
            : part
        )}
      </>
    );
  };

  // Render a single work order row (used by both flat and grouped views)
  // dateGroupQty: when in date-grouped mode, the quantity for this specific date (or null for ungrouped)
  const renderWorkOrderRow = (wo, dateGroupQty = null) => {
    const woHighlights = getHighlightsForWO(wo);
    return (
    <>
      <TableRow
        sx={{
          contentVisibility: 'auto',
          containIntrinsicSize: '0 80px',
          '&:hover': { backgroundColor: 'action.hover' },
          cursor: 'pointer',
          ...(wo.is_multi_step && !wo.can_start && {
            backgroundColor: 'grey.100',
            opacity: 0.8
          })
        }}
        onClick={() => toggleRowExpansion(wo.id)}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1" fontWeight="bold">
                  {wo.work_order_number}
                </Typography>
                <StepIndicator workOrder={wo} />
              </Box>
              {(() => {
                const urgency = getUrgencyLevel(wo);
                return (
                  <Chip
                    label={urgency}
                    color={getPriorityColor(urgency)}
                    size="small"
                    icon={urgency === 'OVERDUE' ? <ErrorIcon /> : undefined}
                  />
                );
              })()}
              <DependencyBlockerAlert workOrder={wo} />
            </Box>
            {expandedRows.has(wo.id) ? <ExpandLess /> : <ExpandMore />}
          </Box>
        </TableCell>

        <TableCell>
          <Typography variant="body1" fontWeight="bold" color="primary">
            {applyHighlight(wo.output_item_name, woHighlights)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {applyHighlight(wo.output_item_display, woHighlights)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            via {wo.operation_display}
          </Typography>
          <RootItemsDisplay workOrder={wo} />
          <LinkedOrdersDisplay workOrder={wo} groupDate={wo._groupDate} />
          {wo.output_item_additional_instructions && (
            <Alert
              severity="warning"
              icon={<Info fontSize="small" />}
              sx={{
                mt: 1, py: 0.5, px: 1,
                '& .MuiAlert-message': { fontSize: '0.8rem', fontWeight: 500 }
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                Additional Instructions:
              </Typography>
              {applyHighlight(wo.output_item_additional_instructions, woHighlights)}
            </Alert>
          )}
        </TableCell>

        <TableCell>
          {dateGroupQty !== null ? (
            <>
              <Typography variant="h5" color="warning.main" fontWeight="bold">
                {Math.round(dateGroupQty)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                for this date
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                ({parseFloat(wo.quantity_to_produce - wo.quantity_completed).toString()} total remaining)
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h5" color="error.main" fontWeight="bold">
                {parseFloat(wo.quantity_to_produce - wo.quantity_completed).toString()}
              </Typography>
              <Typography variant="caption">
                of {parseFloat(wo.quantity_to_produce).toString()} total
              </Typography>
            </>
          )}
        </TableCell>

        <TableCell>
          {wo.demand_summary && wo.demand_summary.total > 0 ? (
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" display="block">Total demand across all orders:</Typography>
                  {wo.demand_summary.overdue > 0 && (
                    <Typography variant="caption" display="block" color="error.light">- {wo.demand_summary.overdue} overdue</Typography>
                  )}
                  {wo.demand_summary.today > 0 && (
                    <Typography variant="caption" display="block" color="error.light">- {wo.demand_summary.today} today</Typography>
                  )}
                  {wo.demand_summary.tomorrow > 0 && (
                    <Typography variant="caption" display="block" color="warning.light">- {wo.demand_summary.tomorrow} tomorrow</Typography>
                  )}
                  {wo.demand_summary.this_week > 0 && (
                    <Typography variant="caption" display="block">- {wo.demand_summary.this_week} this week</Typography>
                  )}
                  {wo.demand_summary.this_month > 0 && (
                    <Typography variant="caption" display="block">- {wo.demand_summary.this_month} this month</Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Box sx={{ cursor: 'help' }}>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {wo.demand_summary.total}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {wo.demand_summary.overdue > 0 && (
                    <Chip label={wo.demand_summary.overdue} size="small" color="error" sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                  )}
                  {wo.demand_summary.today > 0 && (
                    <Chip label={wo.demand_summary.today} size="small" color="error" variant="outlined" sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                  )}
                  {wo.demand_summary.tomorrow > 0 && (
                    <Chip label={wo.demand_summary.tomorrow} size="small" color="warning" sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                  )}
                  {wo.demand_summary.this_week > 0 && (
                    <Chip label={wo.demand_summary.this_week} size="small" color="info" sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  across {wo.demand_timeline?.length || 0} order{wo.demand_timeline?.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            <Box>
              <Typography variant="h5" color="text.secondary">-</Typography>
              <Typography variant="caption" color="text.secondary">No orders</Typography>
            </Box>
          )}
        </TableCell>

        <TableCell>
          {operationCode === 'all'
            ? <Chip label={wo.operation_display || '-'} size="small" color="default" />
            : <MaterialStatusChip workOrder={wo} />
          }
        </TableCell>

        <TableCell>
          <Typography variant="body2">
            {wo.customer_order_ref ? (
              <Chip label={wo.customer_order_ref} size="small" color="info" />
            ) : '-'}
          </Typography>
        </TableCell>

        <TableCell>
          {(() => {
            // In date-grouped view, show the date for this row's demand bucket rather
            // than the WO's static due_date (which is frozen from the first order that
            // spawned the WO and doesn't reflect consolidated demand).
            const displayDate = wo._groupDate || wo.due_date;
            if (!displayDate) {
              return <Typography variant="body2" color="text.secondary">-</Typography>;
            }
            const days = Math.ceil((new Date(displayDate) - new Date()) / (1000 * 60 * 60 * 24));
            const primaryColor = days < 0 || days === 0 ? 'error.main' : days <= 2 ? 'warning.main' : 'text.primary';
            const captionColor = days < 0 || days === 0 ? 'error.main' : days <= 2 ? 'warning.main' : 'text.secondary';
            let captionText;
            if (days < 0) captionText = `${Math.abs(days)}d overdue`;
            else if (days === 0) captionText = 'Due today';
            else if (days === 1) captionText = 'Due tomorrow';
            else captionText = `${days}d remaining`;
            return (
              <Box>
                <Typography variant="body2" fontWeight="bold" color={primaryColor}>
                  {new Date(displayDate).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color={captionColor}>
                  {captionText}
                </Typography>
              </Box>
            );
          })()}
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 100, mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={wo.progress_percentage || 0}
                color={wo.progress_percentage >= 100 ? 'success' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="body2" fontWeight="bold">
              {Math.round(wo.progress_percentage || 0)}%
            </Typography>
          </Box>
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <StockTreeButton workOrder={wo} />
            {user?.is_staff && (
              <>
                <Tooltip title="Admin Edit" arrow>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setAdminEditDialog({ open: true, workOrder: wo }); }}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Work Order" arrow>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, workOrder: wo }); }}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {operationCode !== 'all' && (
              <MaterialAwareStartButton workOrder={wo} onStart={handleStartProduction} />
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0 }}>
          <Collapse in={expandedRows.has(wo.id)} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    <Info /> Work Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>Operation:</strong> {wo.operation_name}
                  </Typography>
                  {wo.is_multi_step && (
                    <>
                      <Typography variant="body2">
                        <strong>Routing Step:</strong> {wo.step_display}
                      </Typography>
                      {wo.depends_on_number && (
                        <Typography variant="body2">
                          <strong>Depends On:</strong> {wo.depends_on_number} ({wo.depends_on_status})
                        </Typography>
                      )}
                    </>
                  )}
                  <Typography variant="body2">
                    <strong>Created:</strong> {new Date(wo.created_at).toLocaleDateString()}
                  </Typography>
                  {wo.due_date && (
                    <Typography variant="body2">
                      <strong>Due:</strong> {new Date(wo.due_date).toLocaleDateString()}
                    </Typography>
                  )}
                  <CollapsibleNotes notes={wo.notes} maxLines={6} />
                  {wo.root_items_display && wo.root_items_display.length > 0 && (
                    <Box>
                      <Typography variant="body2"><strong>For Products:</strong></Typography>
                      <Box sx={{ ml: 2, mt: 0.5 }}>
                        {wo.root_items_display.map((item) => (
                          <Typography key={item.id} variant="body2" sx={{ py: 0.25 }}>- {item.name}</Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <DemandTimelineDisplay workOrder={wo} />
                </Grid>
                <Grid item xs={12}>
                  <MaterialRequirementsTable workOrderId={wo.id} workOrder={wo} />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
  };

  usePageTitle(
    operation 
      ? `${operation.operation_code}` 
      : operationCode === 'all' 
        ? 'All' 
        : 'Shop Floor'
  );

  // Track dialog state
  useEffect(() => {
    dialogOpenRef.current = productionDialog || materialShortageDialog.open || editDialog.open || adminEditDialog.open || deleteDialog.open;
  }, [productionDialog, materialShortageDialog.open, editDialog.open, adminEditDialog.open]);

  // Handle shop floor logout - stay on current page
  const handleShopFloorLogout = async () => {
    try {
      console.log('Shop floor logout initiated');
      await logout();
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  // Load data with optional loading spinner
  const loadData = async (showLoading = true) => {
    // Prevent overlapping calls
    if (loadingInProgress.current) return;
    loadingInProgress.current = true;

    try {
      if (showLoading) {
        setLoading(true);
      }

      // Fetch operations first (needed for building WO query params)
      const operationsResponse = await manufacturingAPI.operations.list();
      const operationsData = operationsResponse.data.results || operationsResponse.data;

      const sortedOperations = [...operationsData].sort((a, b) =>
        naturalSort(a.operation_code, b.operation_code)
      );

      setOperations(sortedOperations);

      const currentOp = sortedOperations.find(op => op.operation_code === operationCode);
      setOperation(currentOp);

      // Build query params - light=true skips expensive demand calculations
      const activeParams = { status: 'active', page_size: 10000, light: 'true' };
      const dateStr = completedDateRef.current.toISOString().split('T')[0]; // YYYY-MM-DD
      const completedParams = { status: 'completed', page_size: 10000, light: 'true', completed_date: dateStr };

      if (operationCode && operationCode !== 'all' && currentOp) {
        activeParams.operation = currentOp.id;
        completedParams.operation = currentOp.id;
      }

      // Fetch active and completed work orders IN PARALLEL
      const [workOrdersResponse, completedResponse] = await Promise.all([
        manufacturingAPI.workOrders.list(activeParams),
        manufacturingAPI.workOrders.list(completedParams)
      ]);

      const filteredOrders = workOrdersResponse.data.results || workOrdersResponse.data;
      filteredOrders.sort((a, b) => {
        const nameA = (a.output_item_name || '').toLowerCase();
        const nameB = (b.output_item_name || '').toLowerCase();
        if (nameA !== nameB) return nameA.localeCompare(nameB);
        return (a.step_number || 1) - (b.step_number || 1);
      });
      setWorkOrders(filteredOrders);

      const filteredCompleted = completedResponse.data.results || completedResponse.data;
      filteredCompleted.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.completed_at || a.created_at);
        const dateB = new Date(b.updated_at || b.completed_at || b.created_at);
        return dateB - dateA;
      });
      setCompletedWorkOrders(filteredCompleted);

    } catch (error) {
      console.error('Error loading shop floor data:', error);
      if (showLoading) {
        toast.error('Failed to load work orders');
      }
    } finally {
      loadingInProgress.current = false;
      if (showLoading) {
        setLoading(false);
      }
      isInitialLoad.current = false;
    }
  };




  // Reload completed orders when date picker changes
  const loadCompletedForDate = async (date) => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const completedParams = { status: 'completed', page_size: 10000, light: 'true', completed_date: dateStr };

      const currentOp = operations.find(op => op.operation_code === operationCode);
      if (operationCode && operationCode !== 'all' && currentOp) {
        completedParams.operation = currentOp.id;
      }

      const completedResponse = await manufacturingAPI.workOrders.list(completedParams);
      const filteredCompleted = completedResponse.data.results || completedResponse.data;
      filteredCompleted.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.completed_at || a.created_at);
        const dateB = new Date(b.updated_at || b.completed_at || b.created_at);
        return dateB - dateA;
      });
      setCompletedWorkOrders(filteredCompleted);
    } catch (error) {
      console.error('Error loading completed orders:', error);
      toast.error('Failed to load completed orders');
    }
  };

  // Auto-logout at 7pm (19:00) - only when time rolls over, not on page load
  useEffect(() => {
    if (!user) return;

    const checkTimeForLogout = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      // Only trigger right at 7pm (19:00-19:00), not after
      if (hours === 19 && minutes === 0) {
        toast.info('Shop floor terminal closes at 7pm. Logging out...');
        handleShopFloorLogout();
      }
    };

    // Check every minute - but NOT immediately on load
    const interval = setInterval(checkTimeForLogout, 60000);

    return () => clearInterval(interval);
  }, [user?.id]);




  useEffect(() => {
    let cancelled = false;
    if (user) {
      // Small delay to let StrictMode cleanup run before starting load
      const timeout = setTimeout(() => {
        if (!cancelled) loadData(true);
      }, 50);

      // Only auto-refresh on specific operation pages, not "all"
      let interval;
      if (operationCode && operationCode !== 'all') {
        interval = setInterval(() => {
          if (!dialogOpenRef.current && !cancelled) {
            loadData(false);
          }
        }, 30000);
      }

      return () => {
        cancelled = true;
        clearTimeout(timeout);
        if (interval) clearInterval(interval);
      };
    }
  }, [operationCode, user?.id]);

  const handleStartProduction = (workOrder) => {
    // Warn if previous step hasn't completed any quantity yet, but allow override
    if (workOrder.is_multi_step && !workOrder.can_start) {
      if (!window.confirm(
        `Step ${workOrder.step_number - 1} (${workOrder.depends_on_number}) hasn't recorded any production yet.\n\n` +
        `Are you sure you want to start this step anyway?`
      )) {
        return;
      }
    }
    setSelectedWorkOrder(workOrder);
    setProductionDialog(true);
  };

  const handleCompleteProduction = async (productionData) => {
    try {
      const quantityCompleted = parseFloat(productionData.quantity_completed) || 0;
      const quantityScrapped = parseFloat(productionData.quantity_scrapped) || 0;
      
      if (quantityCompleted <= 0 || isNaN(quantityCompleted)) {
        toast.error('Please enter quantity completed');
        return;
      }

      // For multi-step: hard block if quantity exceeds what's released from previous step
      if (selectedWorkOrder.is_multi_step && selectedWorkOrder.depends_on_number) {
        const availFromPrev = parseFloat(selectedWorkOrder.qty_available_from_previous_step || 0);
        if (quantityCompleted > availFromPrev) {
          toast.error(`Only ${availFromPrev} units released from Step ${selectedWorkOrder.step_number - 1}. You can't complete more than that.`);
          return;
        }
      }

      try {
        const materialCheckResponse = await manufacturingAPI.workOrders.checkMaterialAvailability(
          selectedWorkOrder.id, 
          quantityCompleted
        );
        
        if (materialCheckResponse.data.shortages && materialCheckResponse.data.shortages.length > 0) {
          setMaterialShortageDialog({
            open: true,
            workOrder: selectedWorkOrder.work_order_number,
            shortages: materialCheckResponse.data.shortages
          });
          return;
        }
        
        if (materialCheckResponse.data.warnings && materialCheckResponse.data.warnings.length > 0) {
          const warnings = materialCheckResponse.data.warnings;
          const overproductionWarning = warnings.find(w => w.type === 'overproduction');
          
          if (overproductionWarning) {
            const proceed = window.confirm(
              `Overproduction detected\n\n` +
              `You're making ${quantityCompleted} units, but only ${selectedWorkOrder.quantity_to_produce - selectedWorkOrder.quantity_completed} are needed for this work order.\n\n` +
              `This will create ${overproductionWarning.extra_units} extra units in stock.\n\n` +
              `Do you want to proceed?`
            );
            
            if (!proceed) {
              toast.info('Production cancelled by user');
              return;
            }
            
            toast.info(`Making ${overproductionWarning.extra_units} extra units for stock`);
          }
        }
        
      } catch (materialError) {
        console.error('Material availability check failed:', materialError);
        toast.error('Cannot verify material availability - production blocked for safety');
        return;
      }

      try {
        // When signed off from the date view, tag this execution with the date
        // worked so the backend can clear that exact date (not the earliest
        // one). The marker is hidden from notes wherever they're displayed.
        const dateMarker = selectedWorkOrder._groupDate
          ? ` [[sfd:${selectedWorkOrder._groupDate.split('T')[0]}]]`
          : '';
        const executionData = {
          work_order: selectedWorkOrder.id,
          start_time: new Date().toISOString(),
          end_time: new Date().toISOString(),
          quantity_completed: quantityCompleted,
          quantity_scrapped: quantityScrapped,
          notes: `${productionData.notes || ''}${dateMarker}`,
          computer_used: `TERMINAL-${operationCode || 'GENERAL'}`
        };

        await manufacturingAPI.workOrderExecutions.create(executionData);
      } catch (executionError) {
        console.error('Work order execution failed:', executionError);
        throw new Error(`Failed to create work order execution: ${executionError.message}`);
      }

      try {
        const updatedQuantityCompleted = parseFloat(selectedWorkOrder.quantity_completed) + quantityCompleted;
        const updatedQuantityScrapped = parseFloat(selectedWorkOrder.quantity_scrapped) + quantityScrapped;

        const workOrderUpdate = {
            quantity_completed: parseFloat(updatedQuantityCompleted.toFixed(4)),
            quantity_scrapped: parseFloat(updatedQuantityScrapped.toFixed(4)),
            ...(productionData.priority_customer_order_id
                ? { priority_customer_order_id: productionData.priority_customer_order_id }
                : {})
        };
        
        await manufacturingAPI.workOrders.update(selectedWorkOrder.id, workOrderUpdate);
      } catch (updateError) {
        console.error('Work order update failed:', updateError);
        console.error('Response data:', updateError.response?.data);
        throw new Error(`Failed to update work order: ${updateError.message}`);
      }

      try {
        await manufacturingAPI.workOrders.consumeMaterials(selectedWorkOrder.id, quantityCompleted);
      } catch (consumeError) {
        console.error('Material consumption failed:', consumeError);
        toast.warning('Production recorded but material consumption failed');
      }

      const isFinalStep = !selectedWorkOrder.is_multi_step
        || selectedWorkOrder.step_number === selectedWorkOrder.total_steps;

      if (quantityCompleted > 0 && isFinalStep) {
        try {
          const stockTransactionData = {
            item_id: selectedWorkOrder.output_item,
            transaction_type: 'production',
            quantity: quantityCompleted,
            unit_cost: 0,
            reference_type: 'work_order',
            reference_id: selectedWorkOrder.id,
            notes: `Production completion: ${selectedWorkOrder.work_order_number}${selectedWorkOrder.is_multi_step ? ` (${selectedWorkOrder.step_display})` : ''}`
          };

          await manufacturingAPI.stockTransactions.create(stockTransactionData);
        } catch (stockError) {
          console.error('Stock transaction failed:', stockError);
          toast.warning('Production recorded but stock update failed');
        }
      }

      // Show step-specific success message
      if (selectedWorkOrder.is_multi_step) {
        if (selectedWorkOrder.step_number < selectedWorkOrder.total_steps) {
          toast.success(`${quantityCompleted} units completed on ${selectedWorkOrder.step_display} - released to Step ${selectedWorkOrder.step_number + 1}`);
        } else {
          toast.success(`Completed final step! ${quantityCompleted} finished units added to stock.`);
        }
      } else {
        toast.success(`Recorded ${quantityCompleted} units completed`);
      }
      
      setProductionDialog(false);
      loadData(true);
      
    } catch (error) {
      console.error('Complete production failed:', error);
      toast.error(error.message || 'Failed to record production');
    }
  };
  
  const handleEditWorkOrder = async (workOrderId, editData) => {
    try {
      // Find the current work order to get the old values
      const currentWO = completedWorkOrders.find(wo => wo.id === workOrderId);
      if (!currentWO) {
        throw new Error('Work order not found');
      }

      const oldQuantityCompleted = currentWO.quantity_completed || 0;
      const newQuantityCompleted = editData.quantity_completed;
      const quantityDifference = newQuantityCompleted - oldQuantityCompleted;

      // Update the work order record
      await manufacturingAPI.workOrders.update(workOrderId, {
        quantity_completed: editData.quantity_completed,
        quantity_scrapped: editData.quantity_scrapped,
        notes: editData.notes
      });

      const isFinalStep = !currentWO.is_multi_step
        || currentWO.step_number === currentWO.total_steps;

      // Adjust stock if quantity changed — only final step affects finished-goods stock
      if (quantityDifference !== 0 && isFinalStep) {
        try {
          const stockTransactionData = {
            item_id: currentWO.output_item,
            transaction_type: quantityDifference > 0 ? 'production' : 'adjustment',
            quantity: quantityDifference, // positive = add stock, negative = remove stock
            unit_cost: 0,
            reference_type: 'work_order',
            reference_id: workOrderId,
            notes: `Work order edit: ${currentWO.work_order_number} (${quantityDifference > 0 ? '+' : ''}${quantityDifference})`
          };

          await manufacturingAPI.stockTransactions.create(stockTransactionData);

          if (quantityDifference > 0) {
            toast.success(`Added ${quantityDifference} units to stock`);
          } else {
            toast.success(`Removed ${Math.abs(quantityDifference)} units from stock`);
          }
        } catch (stockError) {
          console.error('Stock adjustment failed:', stockError);
          toast.warning('Work order updated but stock adjustment failed');
        }
      }
      
      toast.success('Work order updated successfully');
      loadData(true);
    } catch (error) {
      console.error('Failed to update work order:', error);
      throw error;
    }
  };

  const handleAdminEditWorkOrder = async (workOrderId, editData) => {
    try {
      const currentWO = [...workOrders, ...completedWorkOrders].find(wo => wo.id === workOrderId);
      if (!currentWO) {
        throw new Error('Work order not found');
      }

      // Update the work order record only - no stock adjustments
      await manufacturingAPI.workOrders.update(workOrderId, {
        quantity_to_produce: editData.quantity_to_produce,
        quantity_completed: editData.quantity_completed,
        quantity_scrapped: editData.quantity_scrapped,
        customer_order_ref: editData.customer_order_ref,
        notes: editData.notes
      });
      
      toast.success('Work order updated successfully (stock unchanged)');
      loadData(true);
    } catch (error) {
      console.error('Failed to update work order:', error);
      throw error;
    }
  };

  const handleDeleteWorkOrder = async (workOrderId) => {
    try {
      await manufacturingAPI.workOrders.delete(workOrderId);
      toast.success('Work order deleted successfully');
      loadData(true);
    } catch (error) {
      console.error('Failed to delete work order:', error);
      throw error;
    }
  };

  const toggleRowExpansion = (workOrderId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(workOrderId)) {
      newExpanded.delete(workOrderId);
    } else {
      newExpanded.add(workOrderId);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusColor = (status) => {
    const colors = {
      'planned': 'info',
      'released': 'warning',
      'in_progress': 'primary',
      'completed': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (urgency) => {
    switch (urgency) {
      case 'OVERDUE':
        return 'error';
      case 'URGENT':
        return 'error';
      case 'HIGH':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getUrgencyLevel = (workOrder) => {
    if (!workOrder.due_date && workOrder.demand_summary) {
      if (workOrder.demand_summary.overdue > 0) return 'OVERDUE';
      if (workOrder.demand_summary.today > 0) return 'URGENT';
      if (workOrder.demand_summary.tomorrow > 0) return 'URGENT';
      if (workOrder.demand_summary.this_week > 0) return 'HIGH';
      return 'NORMAL';
    }

    if (!workOrder.due_date) return 'NORMAL';

    const now = new Date();
    const dueDate = new Date(workOrder.due_date);
    const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) return 'OVERDUE';
    if (daysUntilDue <= 1) return 'URGENT';
    if (daysUntilDue <= 7) return 'HIGH';
    return 'NORMAL';
  };

  // Filter completed work orders - backend already filters to today
  const filteredCompletedOrders = completedWorkOrders.filter(wo => {
    // Search filter only - date filtering is handled by backend
    if (completedSearchTerm) {
      const search = completedSearchTerm.toLowerCase();
      const matchesSearch =
        wo.work_order_number?.toLowerCase().includes(search) ||
        wo.output_item_name?.toLowerCase().includes(search) ||
        wo.output_item_display?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    return true;
  });

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!user) {
    return (
      <ShopFloorLogin
        operationCode={operationCode}
        operationName={operation ? `${operation.operation_code} - ${operation.name}` : null}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Card sx={{ mb: 3, border: operation ? '2px solid #1976d2' : 'none' }}>
        <CardContent>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Build color="primary" />
                Shop Floor Terminal
                {user?.is_staff && (
                  <Chip label="ADMIN" size="small" color="error" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {operation ? `${operation.operation_code} - ${operation.name}` : 
                 operationCode === 'all' ? 'All Operations' :
                 operationCode ? `Operation ${operationCode}` : 'All Operations'}
              </Typography>
              {operation && operation.work_instructions && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Instructions:</strong> {operation.work_instructions}
                  </Typography>
                </Alert>
              )}
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body1">
                  <strong>{user.first_name} {user.last_name}</strong>
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => loadData(true)}
                  disabled={loading}
                  size="small"
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ExitToApp />}
                  onClick={handleShopFloorLogout}
                  color="error"
                  size="small"
                >
                  Logout
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Text Highlighter bar */}
      {user?.is_staff && (
        <Card sx={{ mb: 3, border: (showHighlightBar || showGlobalHighlightBar) ? '2px solid #ff9800' : '1px solid #e0e0e0' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            {/* Toggle buttons + summary chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {user?.is_staff && (
                <>
                  <Button
                    startIcon={<Search />}
                    onClick={() => {
                      setShowGlobalHighlightBar(prev => !prev);
                      if (!showGlobalHighlightBar && globalHighlights.length === 0) {
                        setGlobalHighlights(['']);
                      }
                    }}
                    size="small"
                    variant={showGlobalHighlightBar ? 'contained' : 'outlined'}
                    color="warning"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Global Highlights {globalHighlights.filter(g => g.trim()).length > 0
                      ? `(${globalHighlights.filter(g => g.trim()).length})`
                      : ''}
                  </Button>
                  <Button
                    startIcon={<FormatPaint />}
                    onClick={() => {
                      setShowHighlightBar(prev => !prev);
                      if (!showHighlightBar && highlightRules.length === 0) {
                        setHighlightRules([{ productCode: '', highlightText: '' }]);
                      }
                    }}
                    size="small"
                    variant={showHighlightBar ? 'contained' : 'outlined'}
                    color="warning"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Product Code Rules {highlightRules.filter(r => r.productCode.trim() && r.highlightText.trim()).length > 0
                      ? `(${highlightRules.filter(r => r.productCode.trim() && r.highlightText.trim()).length})`
                      : ''}
                  </Button>
                </>
              )}
              {/* Summary chips when collapsed (visible to everyone) */}
              {!showGlobalHighlightBar && globalHighlights.filter(g => g.trim()).length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {globalHighlights.filter(g => g.trim()).map((g, i) => (
                    <Chip
                      key={`g-${i}`}
                      label={<mark style={{ backgroundColor: '#ffeb3b', padding: '0 3px', borderRadius: '2px' }}>{g.trim()}</mark>}
                      size="small"
                      variant="outlined"
                      color="warning"
                    />
                  ))}
                </Box>
              )}
              {!showHighlightBar && highlightRules.filter(r => r.productCode.trim() && r.highlightText.trim()).length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {highlightRules.filter(r => r.productCode.trim() && r.highlightText.trim()).map((rule, i) => (
                    <Chip
                      key={`p-${i}`}
                      label={<>
                        <strong>{rule.productCode}</strong>
                        {' \u2192 '}
                        <mark style={{ backgroundColor: '#ffeb3b', padding: '0 2px', borderRadius: '2px' }}>{rule.highlightText}</mark>
                      </>}
                      size="small"
                      variant="outlined"
                      color="warning"
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Global highlights editor (admin only) */}
            {user?.is_staff && (
              <Collapse in={showGlobalHighlightBar}>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Highlight text across all work orders (persisted, visible to all users)
                </Typography>
                <Box>
                  {globalHighlights.map((text, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        label="Text to Highlight"
                        placeholder="e.g. 635"
                        value={text}
                        onChange={(e) => updateGlobalHighlight(index, e.target.value)}
                        sx={{ flex: 1, maxWidth: 300 }}
                        InputProps={{
                          endAdornment: text.trim() ? (
                            <InputAdornment position="end">
                              <mark style={{ backgroundColor: '#ffeb3b', padding: '0 4px', borderRadius: '2px', fontSize: '0.8rem' }}>{text.trim()}</mark>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                      <IconButton size="small" color="error" onClick={() => removeGlobalHighlight(index)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button startIcon={<Add />} size="small" onClick={addGlobalHighlight} variant="outlined" sx={{ textTransform: 'none' }}>
                      Add
                    </Button>
                    {globalHighlights.length > 0 && (
                      <Button
                        size="small"
                        onClick={() => { setGlobalHighlights([]); saveAllHighlights(highlightRules, []); }}
                        color="error"
                        variant="text"
                        sx={{ textTransform: 'none' }}
                      >
                        Clear All
                      </Button>
                    )}
                  </Box>
                </Box>
              </Collapse>
            )}

            {/* Product code rules editor (admin only) */}
            {user?.is_staff && (
              <Collapse in={showHighlightBar}>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Highlight specific text within matching product codes (persisted, visible to all users)
                </Typography>
                <Box>
                  {highlightRules.map((rule, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        label="Product Code"
                        placeholder="e.g. EN/AA25"
                        value={rule.productCode}
                        onChange={(e) => updateHighlightRule(index, 'productCode', e.target.value)}
                        sx={{ flex: 1, maxWidth: 250 }}
                      />
                      <TextField
                        size="small"
                        label="Text to Highlight"
                        placeholder="e.g. 635"
                        value={rule.highlightText}
                        onChange={(e) => updateHighlightRule(index, 'highlightText', e.target.value)}
                        sx={{ flex: 1, maxWidth: 250 }}
                        InputProps={{
                          endAdornment: rule.highlightText ? (
                            <InputAdornment position="end">
                              <mark style={{ backgroundColor: '#ffeb3b', padding: '0 4px', borderRadius: '2px', fontSize: '0.8rem' }}>{rule.highlightText}</mark>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                      <IconButton size="small" color="error" onClick={() => removeHighlightRule(index)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button startIcon={<Add />} size="small" onClick={addHighlightRule} variant="outlined" sx={{ textTransform: 'none' }}>
                      Add Rule
                    </Button>
                    {highlightRules.length > 0 && (
                      <Button
                        size="small"
                        onClick={() => { setHighlightRules([]); saveAllHighlights([], globalHighlights); }}
                        color="error"
                        variant="text"
                        sx={{ textTransform: 'none' }}
                      >
                        Clear All
                      </Button>
                    )}
                  </Box>
                </Box>
              </Collapse>
            )}
          </CardContent>
        </Card>
      )}

      {!loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Station Quick Switch</Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Button
                  variant={!operationCode || operationCode === 'all' ? "contained" : "outlined"}
                  onClick={() => navigate('/shop-floor/all')}
                  size="small"
                >
                  All Operations
                </Button>
              </Grid>
              {operations.map((op) => (
                <Grid item key={op.id}>
                  <Button
                    variant={operationCode === op.operation_code ? "contained" : "outlined"}
                    onClick={() => navigate(`/shop-floor/${op.operation_code}`)}
                    size="small"
                  >
                    {op.operation_code} - {op.name}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          {/* Tabs for Active vs Completed */}
          <Tabs 
            value={activeTab} 
            onChange={(e, newVal) => setActiveTab(newVal)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment />
                  Active Work Orders
                  <Chip label={workOrders.length} size="small" color="primary" />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History />
                  Completed
                  <Chip label={completedWorkOrders.length} size="small" color="success" />
                </Box>
              } 
            />
          </Tabs>

          {/* Active Work Orders Tab */}
          {activeTab === 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment />
                  Available Work Orders ({workOrders.length})
                  {workOrders.filter(wo => wo.priority <= 2).length > 0 && (
                    <Chip 
                      label={`${workOrders.filter(wo => wo.priority <= 2).length} URGENT`} 
                      color="error" 
                      size="small"
                      icon={<Warning />}
                    />
                  )}
                  {workOrders.filter(wo => wo.is_multi_step).length > 0 && (
                    <Chip 
                      label={`${workOrders.filter(wo => wo.is_multi_step).length} Multi-Step`} 
                      color="info" 
                      size="small"
                      icon={<LinkIcon />}
                      variant="outlined"
                    />
                  )}
                </Typography>
                
                <TextField
                  select
                  size="small"
                  value={sortOrder}
                  onChange={(e) => handleSortChange(e.target.value)}
                  label="Sort by"
                  sx={{ minWidth: 200 }}
                  SelectProps={{
                    native: true,
                  }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: 1,
                  borderColor: groupByDate ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                }}>
                  <Button
                    startIcon={<CalendarMonth />}
                    onClick={handleGroupByDateToggle}
                    size="small"
                    variant={groupByDate ? 'contained' : 'text'}
                    color={groupByDate ? 'primary' : 'inherit'}
                    sx={{
                      borderRadius: 0,
                      textTransform: 'none',
                      px: 1.5,
                      minHeight: 36,
                      fontWeight: groupByDate ? 600 : 400,
                      ...(groupByDate ? {} : { color: 'text.secondary' }),
                    }}
                  >
                    {groupByDate ? 'By Date' : 'Group by Date'}
                  </Button>
                  {groupByDate && (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                      <DatePicker
                        value={filterDate}
                        onChange={handleFilterDateChange}
                        slotProps={{
                          textField: {
                            size: 'small',
                            placeholder: 'All dates',
                            sx: {
                              width: 150,
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '& .MuiInputBase-root': { borderRadius: 0, pl: 0.5 },
                              '& .MuiInputBase-input': { py: '6px', fontSize: '0.85rem' },
                            },
                          },
                          field: { clearable: true },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Box>
              </Box>

              
              
              {loading ? (
                <LinearProgress />
              ) : workOrders.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="h6">No work orders available</Typography>
                  <Typography>
                    No work orders are released for this operation. Check with your supervisor or try refreshing.
                  </Typography>
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Work Order & Urgency</TableCell>
                        <TableCell>What to Make</TableCell>
                        <TableCell>Need Now</TableCell>
                        <TableCell>Total Demand</TableCell>
                        <TableCell>Material Status</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const groups = getGroupedWorkOrders(workOrders);
                        if (groups) {
                          if (groups.length === 0 && filterDate) {
                            return (
                              <TableRow>
                                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                                  <Typography color="text.secondary">
                                    No work orders due on {filterDate.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          }
                          // Grouped by date rendering - same WO can appear under multiple dates
                          return groups.map((group, groupIdx) => {
                            const color = getDateGroupColor(group.date);
                            const days = group.date && group.date !== 'no-date' ? Math.ceil((new Date(group.date) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                            const isUrgent = color === 'error.main';
                            const isWarning = color === 'warning.main';
                            const urgencyLabel = isUrgent ? (days !== null && days < 0 ? 'OVERDUE' : 'TODAY') : isWarning ? 'TOMORROW' : null;
                            // Solid background color for the banner
                            const bannerBg = isUrgent ? 'error.main' : isWarning ? 'warning.main' : color === 'info.main' ? 'info.main' : 'grey.700';
                            const bannerText = isWarning ? 'warning.contrastText' : '#fff';
                            return (
                            <React.Fragment key={group.date || group.label}>
                              {/* Spacer between date groups */}
                              <TableRow>
                                <TableCell colSpan={9} sx={{ p: 0, border: 'none', height: groupIdx > 0 ? 24 : 4, background: 'transparent' }} />
                              </TableRow>
                              {/* Date group banner */}
                              <TableRow>
                                <TableCell colSpan={9} sx={{ p: 0, border: 'none' }}>
                                  <Box sx={{
                                    bgcolor: bannerBg,
                                    color: bannerText,
                                    py: 1.2,
                                    px: 2.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    borderRadius: '6px 6px 0 0',
                                  }}>
                                    <CalendarMonth sx={{ fontSize: '1.5rem' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.02em' }}>
                                      {group.label}
                                    </Typography>
                                    {urgencyLabel && (
                                      <Chip
                                        label={urgencyLabel}
                                        size="small"
                                        sx={{
                                          height: 22,
                                          fontSize: '0.7rem',
                                          fontWeight: 800,
                                          bgcolor: 'rgba(255,255,255,0.25)',
                                          color: bannerText,
                                          letterSpacing: '0.05em',
                                        }}
                                      />
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 500 }}>
                                      {group.orders.length} work order{group.orders.length !== 1 ? 's' : ''}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {group.orders.map((wo) => (
                                <React.Fragment key={wo._virtualKey || wo.id}>
                                  {renderWorkOrderRow(wo, wo._groupQty)}
                                </React.Fragment>
                              ))}
                            </React.Fragment>
                          );
                          });
                        }
                        // Flat rendering (no grouping)
                        return getSortedWorkOrders(workOrders).map((wo) => (
                          <React.Fragment key={wo.id}>
                            {renderWorkOrderRow(wo)}
                          </React.Fragment>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Completed Work Orders Tab */}
          {activeTab === 1 && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  placeholder="Search work orders..."
                  size="small"
                  value={completedSearchTerm}
                  onChange={(e) => setCompletedSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )
                  }}
                  sx={{ minWidth: 250 }}
                />

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DatePicker
                    label="Date"
                    value={completedDate}
                    onChange={(newDate) => {
                      if (newDate) {
                        setCompletedDate(newDate);
                        loadCompletedForDate(newDate);
                      }
                    }}
                    maxDate={new Date()}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 170 } } }}
                  />
                </LocalizationProvider>

                <Button
                  size="small"
                  variant={completedDate.toDateString() === new Date().toDateString() ? 'contained' : 'outlined'}
                  onClick={() => {
                    const today = new Date();
                    setCompletedDate(today);
                    loadCompletedForDate(today);
                  }}
                >
                  Today
                </Button>

                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  Showing {filteredCompletedOrders.length} of {completedWorkOrders.length} orders for {completedDate.toLocaleDateString()}
                </Typography>
              </Box>

              {loading ? (
                <LinearProgress />
              ) : filteredCompletedOrders.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="h6">No completed work orders found</Typography>
                  <Typography>
                    {completedSearchTerm
                      ? 'Try adjusting your search.'
                      : `No work orders completed on ${completedDate.toLocaleDateString()}.`}
                  </Typography>
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Work Order</TableCell>
                        <TableCell>Output Item</TableCell>
                        <TableCell>Day's Output</TableCell>
                        <TableCell>Day's Scrap</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCompletedOrders.map((wo) => {
                        const completedDate = new Date(wo.updated_at || wo.completed_at || wo.created_at);
                        const canEdit = isToday(wo.updated_at || wo.completed_at);
                        const completedHighlights = getHighlightsForWO(wo);

                        return (
                          <TableRow key={wo.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body1" fontWeight="bold">
                                  {wo.work_order_number}
                                </Typography>
                                <StepIndicator workOrder={wo} />
                              </Box>
                              <Chip
                                label={wo.status === 'completed' ? 'COMPLETED' : 'IN PROGRESS'}
                                size="small"
                                color={wo.status === 'completed' ? 'success' : 'warning'}
                                icon={wo.status === 'completed' ? <CheckCircle /> : <Build />}
                              />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body1" fontWeight="bold">
                                {applyHighlight(wo.output_item_name, completedHighlights)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {applyHighlight(wo.output_item_display, completedHighlights)}
                              </Typography>
                              {/* Show root items in completed tab too */}
                              <RootItemsDisplay workOrder={wo} />
                            </TableCell>

                            <TableCell>
                              <Typography variant="h6" color="success.main" fontWeight="bold">
                                {parseFloat(wo.today_quantity_completed || 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                today ({parseFloat(wo.quantity_completed)} of {parseFloat(wo.quantity_to_produce)} total)
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {parseFloat(wo.today_quantity_scrapped || 0) > 0 ? (
                                <Typography variant="body1" color="error.main" fontWeight="bold">
                                  {parseFloat(wo.today_quantity_scrapped)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">
                                {completedDate.toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {completedDate.toLocaleTimeString()}
                              </Typography>
                              {canEdit && (
                                <Chip 
                                  label="Today" 
                                  size="small" 
                                  color="info" 
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </TableCell>

                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  maxWidth: 200, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={wo.notes}
                              >
                                {wo.notes || '-'}
                              </Typography>
                            </TableCell>
                            
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <StockTreeButton workOrder={wo} />
                                {user?.is_staff ? (
                                  <Tooltip title="Admin Edit" arrow>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setAdminEditDialog({ open: true, workOrder: wo })}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                ) : canEdit ? (
                                  <Tooltip title="Edit (today's orders only)" arrow>
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => setEditDialog({ open: true, workOrder: wo })}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Cannot edit - not completed today" arrow>
                                    <span>
                                      <IconButton size="small" disabled>
                                        <Edit />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <EnhancedProductionDialog
        open={productionDialog}
        onClose={() => setProductionDialog(false)}
        selectedWorkOrder={selectedWorkOrder}
        onComplete={handleCompleteProduction}
      />

      <MaterialShortageDialog
        open={materialShortageDialog.open}
        onClose={() => setMaterialShortageDialog({ open: false, workOrder: null, shortages: [] })}
        workOrder={materialShortageDialog.workOrder}
        shortages={materialShortageDialog.shortages || []}
      />

      <EditCompletedWorkOrderDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, workOrder: null })}
        workOrder={editDialog.workOrder}
        onSave={handleEditWorkOrder}
      />

      <AdminEditWorkOrderDialog
        open={adminEditDialog.open}
        onClose={() => setAdminEditDialog({ open: false, workOrder: null })}
        workOrder={adminEditDialog.workOrder}
        onSave={handleAdminEditWorkOrder}
      />

      <DeleteWorkOrderDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, workOrder: null })}
        workOrder={deleteDialog.workOrder}
        onDelete={handleDeleteWorkOrder}
      />
    </Box>
  );
}